import React from 'react';
import {
  Button,
  ButtonGroup,
  Card,
  CardTitleWithIcon,
  PlanCurrentUsage,
  PlanPastUsage,
  Subtitle,
  TrevorIcon,
} from '@trevor/ui-components';
import {
  getPlanUsageIntervalsEnterprise,
  getPlanUsageIntervalsPro,
} from '@/features/credits/components/YourPlanUsage/YourPlanUsage.utils';
import { useDatasourceId } from '@/hooks';
import { Link } from 'react-router-dom';
import {
  useGetCreditsConsumptionInfoQuery,
  useGetCurrentBillingConsumptionQuery,
  useGetPastBillingConsumptionQuery,
  useGetSubscriptionDetailsQuery,
} from '@/features/credits/apiSlice';
import { SUBSCRIPTION } from '@/features/credits/utils';
import {
  BillingBanner,
  HeaderContainer,
  PlanContainer,
} from '@/features/credits/components/YourPlanUsage/YourPlanUsage.styles';
import { getDateInterval } from '@/features/credits/components/utils';

export const YourPlanUsage = () => {
  const datasourceId = useDatasourceId();
  const { data: subscription } = useGetSubscriptionDetailsQuery(datasourceId);

  const skipBillingApis =
    !subscription || subscription.name === SUBSCRIPTION.free;

  const { data: currentBillingConsumption } =
    useGetCurrentBillingConsumptionQuery(datasourceId, {
      skip: skipBillingApis,
    });
  const { data: pastBillingConsumption = [] } =
    useGetPastBillingConsumptionQuery(datasourceId, {
      skip: skipBillingApis,
    });
  const { data: creditConsumptionInfo } =
    useGetCreditsConsumptionInfoQuery(datasourceId);

  const [showPreviousCycles, setShowPreviousCycles] = React.useState(false);

  if (
    !currentBillingConsumption ||
    !pastBillingConsumption ||
    !creditConsumptionInfo ||
    !subscription ||
    subscription.name === SUBSCRIPTION.free
  ) {
    return null;
  }

  const intervals =
    subscription.name === SUBSCRIPTION.pro
      ? getPlanUsageIntervalsPro()
      : getPlanUsageIntervalsEnterprise();

  return (
    <Card width="100%" padding={48} gap={16}>
      <HeaderContainer>
        <CardTitleWithIcon
          title="Your Pro Plan usage"
          iconName="illustration-dashboard"
        />
        <ButtonGroup>
          <Button
            variant="secondary"
            active={!showPreviousCycles}
            onClick={() => setShowPreviousCycles(false)}
          >
            This cycle
          </Button>
          <Button
            variant="secondary"
            disabled={pastBillingConsumption.length === 0}
            active={pastBillingConsumption.length === 0 && showPreviousCycles}
            onClick={() => setShowPreviousCycles(true)}
          >
            Previous cycle
          </Button>
        </ButtonGroup>
      </HeaderContainer>
      <PlanContainer>
        <Subtitle variant={2}>
          This billing cycle ({' '}
          {getDateInterval(
            currentBillingConsumption.dateFrom,
            currentBillingConsumption.dateTo,
          )}
          )
        </Subtitle>
        <PlanCurrentUsage
          intervals={intervals}
          forecastUsage={creditConsumptionInfo.totalConsumedCredits}
          currentUsage={currentBillingConsumption.creditUsed}
        />
      </PlanContainer>
      <BillingBanner>
        *The forecast is based on the credits you’ve used in the last 30 days. A
        complete cost breakdown (including tax) can be seen on the{' '}
        <Link to={`/datasources/${datasourceId}/billing`}>billing page.</Link>
      </BillingBanner>
      {showPreviousCycles && (
        <>
          <Subtitle variant={2}>Previous billing cycles</Subtitle>
          {pastBillingConsumption.map((past) => (
            <PlanContainer key={past.dateFrom}>
              <Subtitle variant={2}>
                {getDateInterval(past.dateFrom, past.dateTo)}
              </Subtitle>
              <PlanPastUsage
                intervals={intervals}
                pastUsage={past.creditUsed}
              />
            </PlanContainer>
          ))}
          <Link to={`/datasources/${datasourceId}/billing`}>
            <Button icon={<TrevorIcon name="receipt" />} variant="secondary">
              See invoices
            </Button>
          </Link>
        </>
      )}
    </Card>
  );
};
